import {useCallback, useContext, useEffect, useMemo} from 'preact/hooks';

import {debounce} from 'utilities/common/helpers';
import {ReportType, useErrorLogger} from 'utilities/error-logger';
import {useTrackErrorOnlyOnce} from 'utilities/tracking/hooks';

import {
  fetchProductDetails,
  resolveSellingPlanId,
  resolveVariant,
} from './helpers';
import {ProductDetailsContextImpl} from './context';
import {UnableToResolveVariantError} from './errors';

export function useProductDetails() {
  return useContext(ProductDetailsContextImpl);
}

export function useFetchProductDetails() {
  const {reportError} = useErrorLogger();

  const {setProductDetails} = useProductDetails();

  return useCallback(async () => {
    try {
      const result = await fetchProductDetails();
      setProductDetails(result);
      return result;
    } catch (error) {
      reportError({
        severity: ReportType.Metric,
        error,
        message: 'Failed to fetch product details',
      });
    }

    return null;
  }, [reportError, setProductDetails]);
}

export function useFetchVariantId() {
  const fetchProductDetails = useFetchProductDetails();

  const trackErrorOnlyOnce = useTrackErrorOnlyOnce();

  return useCallback(async () => {
    return fetchProductDetails()
      .then((productDetails) => resolveVariant(productDetails))
      .then((variant) => {
        if (variant && variant.available) {
          return variant.id;
        }
      })
      .catch((error: unknown) => {
        if (error instanceof UnableToResolveVariantError) {
          // this may be triggered multiple times, on each CHANGE_EVENTS, so reporting error will be overwhelming
          trackErrorOnlyOnce(
            '',
            error,
            'Failed to resolve variant',
            ReportType.Expected,
          );
          return undefined;
        }

        throw error;
      });
  }, [fetchProductDetails, trackErrorOnlyOnce]);
}

export const PRODUCT_CHANGE_EVENTS = ['mouseup', 'keyup', 'touchend', 'change'];

export enum ProductProperty {
  Variant = 'variant',
  SellingPlan = 'sellingPlan',
}

export function useProductPropertyChangeEvents(
  properties: ProductProperty[] = [
    ProductProperty.Variant,
    ProductProperty.SellingPlan,
  ],
) {
  const {setVariantId, setSellingPlanId} = useProductDetails();
  const fetchVariantId = useFetchVariantId();

  const handleVariantChange = useCallback(async () => {
    const newVariantId = await fetchVariantId();
    setVariantId((currentVariantId) => {
      if (currentVariantId !== newVariantId) {
        return newVariantId;
      }
      return currentVariantId;
    });
  }, [fetchVariantId, setVariantId]);

  const handleSellingPlanChange = useCallback(() => {
    const newSellingPlanId = resolveSellingPlanId();
    setSellingPlanId((currentSellingPlanId) => {
      if (currentSellingPlanId !== newSellingPlanId) {
        return newSellingPlanId;
      }
      return currentSellingPlanId;
    });
  }, [setSellingPlanId]);

  const propertyHandlerMap: {
    [key in ProductProperty]: () => void;
  } = useMemo(
    () => ({
      sellingPlan: debounce(handleSellingPlanChange),
      variant: debounce(handleVariantChange),
    }),
    [handleSellingPlanChange, handleVariantChange],
  );

  const attachProductChangeEventListeners = useCallback(() => {
    PRODUCT_CHANGE_EVENTS.forEach((type) => {
      properties.forEach((property) => {
        window.addEventListener(type, propertyHandlerMap[property]);
      });
    });
  }, [properties, propertyHandlerMap]);

  const detachProductChangeEventListeners = useCallback(() => {
    PRODUCT_CHANGE_EVENTS.forEach((type) => {
      properties.forEach((property) => {
        window.removeEventListener(type, propertyHandlerMap[property]);
      });
    });
  }, [properties, propertyHandlerMap]);

  useEffect(() => {
    properties.forEach((property) => {
      propertyHandlerMap[property]();
    });
  }, [properties, propertyHandlerMap]);

  useEffect(() => {
    attachProductChangeEventListeners();

    return () => {
      detachProductChangeEventListeners();
    };
  }, [attachProductChangeEventListeners, detachProductChangeEventListeners]);
}
